import React, { useEffect, useMemo, useState } from 'react'
import config from '@/config'
import { makeStyles } from '@mui/styles'
import { ModalBig } from '@/modules/base/components/modal/index'
import { toast } from '@/modules/base/components/toast/toastConfig'
import lang from '@/lang'
import AccessUser from './accessUser'
import { ButtonIconSquare, CancelButton } from '../../buttons'
import errorCodesEnum from '@/enums/error-codes.enum'
import { UserSelect } from '@/modules/user/components/user-select/user-select'
import { UserService } from '@/modules/user/userService'
import request from '@/modules/base/util/request'
import {
  ACCESS_LEVELS,
  ASSIGNABLE_RELATION,
  MIN_EXCLUDED_LEVEL,
  buildAccessChangeSet,
  buildDirectAccessKey,
  buildDraftAccessData,
  isInheritedAccess,
  isRemovableAccess,
} from './helpers'

const { folderAccessPermissions } = lang

interface AccessModalStyleProps {
  removeAllDisabled: boolean;
}

const useStyles = makeStyles((theme: Theme) => {
  return {
    modal: {
      width: 1050,
      height: 660,
      minHeight: 660,
      padding: 20,
    },
    modalContent: {
      marginTop: 20,
    },
    usersWithAccess: {
      marginBottom: 12,
      marginTop: 20,
      fontSize: theme.fontSizes.smedium,
      fontWeight: theme.fontWeights.semiBold,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      color: theme.textSecondary,
    },
    removeAccessForAll: ({ removeAllDisabled }: AccessModalStyleProps) => ({
      cursor: 'pointer',
      fontSize: theme.fontSizes.default,
      fontWeight: theme.fontWeights.regular,
      color: removeAllDisabled ? theme.primary : theme.red,
    }),
    userSelect: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      paddingTop: 0,
      paddingBottom: 4,
      gap: 5,
    },
    accessList: {
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
    },
  }
})

interface AccessModalProps {
  open: boolean;
  type: string;
  id: string;
  onClose: () => void;
  folderName: string;
}

const AccessModal: React.FC<AccessModalProps> = (props) => {
  const {
    open,
    type,
    id,
    onClose,
    folderName,
  } = props
  const [initialAccessData, setInitialAccessData] = useState([])
  const [draftAddedAccess, setDraftAddedAccess] = useState([])
  const [draftRemovedAccessKeys, setDraftRemovedAccessKeys] = useState([])
  const [newUser, setNewUser] = useState(null)
  const [isSaving, setIsSaving] = useState(false)

  const accessData = useMemo(() => {
    return buildDraftAccessData(initialAccessData, draftAddedAccess, draftRemovedAccessKeys)
  }, [initialAccessData, draftAddedAccess, draftRemovedAccessKeys])

  const hasPendingChanges = draftAddedAccess.length > 0 || draftRemovedAccessKeys.length > 0

  const resetDraft = () => {
    setDraftAddedAccess([])
    setDraftRemovedAccessKeys([])
    setNewUser(null)
  }

  const fetchAccessList = () => {
    request(`${config.resourceHost}access/${type}/${id}/definition`, {
      isConcurrent: true,
      auth: true,
      method: 'GET',
    }).then((response) => {
      const { data } = response.body
      setInitialAccessData(data)
      resetDraft()
    }).catch(() => {
      toast.showToast({ type: 'error', message: lang.defaultErrorMessage })
    })
  }

  const deleteAccess = (userId, userType, relation) => {
    return request(`${config.resourceHost}access/${type}/${id}/definition`, {
      isConcurrent: true,
      auth: true,
      method: 'DELETE',
      data: {
        userId,
        userType,
        relation,
      },
    })
  }

  const createAccess = (userId, userType, relation) => {
    return request(`${config.resourceHost}access/${type}/${id}/definition`, {
      isConcurrent: true,
      auth: true,
      method: 'POST',
      data: {
        userId,
        userType,
        relation,
      },
    })
  }

  const onDelete = (userId, _userType, _userSubType, relation) => {
    const directAccessKey = buildDirectAccessKey({ id: userId, relation })

    setDraftAddedAccess((currentDraftAddedAccess) => {
      const updatedDraftAddedAccess = currentDraftAddedAccess.filter((item) => buildDirectAccessKey(item) !== directAccessKey)

      if (updatedDraftAddedAccess.length !== currentDraftAddedAccess.length) {
        return updatedDraftAddedAccess
      }

      if (initialAccessData.some((item) => buildDirectAccessKey(item) === directAccessKey && isRemovableAccess(item))) {
        setDraftRemovedAccessKeys((currentDraftRemovedAccessKeys) => {
          if (currentDraftRemovedAccessKeys.includes(directAccessKey)) {
            return currentDraftRemovedAccessKeys
          }

          return [...currentDraftRemovedAccessKeys, directAccessKey]
        })
      }

      return currentDraftAddedAccess
    })
  }

  const onDeleteAll = () => {
    setDraftAddedAccess([])
    setDraftRemovedAccessKeys(
      initialAccessData
        .filter(isRemovableAccess)
        .map((item) => buildDirectAccessKey(item)),
    )
  }

  const onAddAll = (options, relation) => {
    const existingKeys = new Set(accessData.map((item) => buildDirectAccessKey(item)))
    const initialKeys = new Set(
      initialAccessData
        .filter(isRemovableAccess)
        .map((item) => buildDirectAccessKey(item)),
    )
    const reactivatedKeys = new Set()
    const addedEntries = options.reduce((result, option) => {
      const directAccessKey = buildDirectAccessKey({ id: option.id, relation })

      if (existingKeys.has(directAccessKey)) {
        return result
      }

      if (initialKeys.has(directAccessKey)) {
        reactivatedKeys.add(directAccessKey)
        return result
      }

      result.push({
        id: option.id,
        name: option.name,
        relation,
        inherited: false,
      })
      return result
    }, [])

    if (!reactivatedKeys.size && !addedEntries.length) {
      return
    }

    setDraftRemovedAccessKeys((currentDraftRemovedAccessKeys) =>
      currentDraftRemovedAccessKeys.filter((item) => !reactivatedKeys.has(item)),
    )

    if (addedEntries.length) {
      setDraftAddedAccess((currentDraftAddedAccess) => [...currentDraftAddedAccess, ...addedEntries])
    }
  }

  const onCreate = (userId, _userType, _userSubType, relation) => {
    const directAccessKey = buildDirectAccessKey({ id: userId, relation })
    const existingDraftAccess = accessData.some((item) => buildDirectAccessKey(item) === directAccessKey)

    if (existingDraftAccess) {
      toast.showToast({ type: 'error', message: folderAccessPermissions.alreadyExists })
      return
    }

    const accessAlreadyExists = initialAccessData.some((item) => buildDirectAccessKey(item) === directAccessKey && isRemovableAccess(item))
    if (accessAlreadyExists) {
      setDraftRemovedAccessKeys((currentDraftRemovedAccessKeys) =>
        currentDraftRemovedAccessKeys.filter((item) => item !== directAccessKey),
      )
      return
    }

    const accessName = newUser?.id === userId ? newUser.name : null

    setDraftRemovedAccessKeys((currentDraftRemovedAccessKeys) =>
      currentDraftRemovedAccessKeys.filter((item) => item !== directAccessKey),
    )
    setDraftAddedAccess((currentDraftAddedAccess) => {
      if (currentDraftAddedAccess.some((item) => buildDirectAccessKey(item) === directAccessKey)) {
        return currentDraftAddedAccess
      }

      return [...currentDraftAddedAccess, {
        id: userId,
        name: accessName,
        relation,
        inherited: false,
      }]
    })
  }

  const onSave = async() => {
    if (!hasPendingChanges || isSaving) {
      onClose()
      return
    }

    const { additions, deletions } = buildAccessChangeSet(initialAccessData, draftAddedAccess, draftRemovedAccessKeys)
    setIsSaving(true)

    const results = await Promise.allSettled([
      ...deletions.map((item) => deleteAccess(item.id, 'user', item.relation)),
      ...additions.map((item) => createAccess(item.id, 'user', item.relation)),
    ])

    const hasErrors = results.some((result) => result.status === 'rejected')
    setIsSaving(false)

    if (hasErrors) {
      const existsError = results.some((result) => {
        return result.status === 'rejected' && result.reason?.body?.meta?.resource?.[0]?.code === errorCodesEnum.EXISTS
      })

      toast.showToast({
        type: 'error',
        message: existsError ? folderAccessPermissions.alreadyExists : lang.defaultErrorMessage,
      })
      fetchAccessList()
      return
    }

    toast.showToast({ message: lang.changeSuccess })
    onClose()
  }

  useEffect(() => {
    fetchAccessList()
  }, [])
  const classes = useStyles()

  const actions = [
    <CancelButton
      label={folderAccessPermissions.cancel}
      key='cancel'
      disabled={isSaving}
      onClick={onClose}
    />,
    <ButtonIconSquare
      label={folderAccessPermissions.save}
      key='save'
      disabled={isSaving}
      onClick={onSave}
    />,
  ]

  const accessList = accessData.map((item) => {
    return (
      <AccessUser
        key={`${item.id}-${item.relation}-${item.inherited ? 'inherited' : 'direct'}`}
        name={item.name}
        type={item.relation}
        id={item.id}
        inherited={isInheritedAccess(item)}
        onDelete={() => onDelete(item.id, 'user', undefined, item.relation)}
      />
    )
  })

  return (
    <ModalBig
      title={`${folderAccessPermissions.title} - ${folderName}`}
      actions={actions}
      open={open}
      className={classes.modal}
      showCloseIcon={false}
      maxWidth={false}>
      <div className={classes.modalContent}>
        <AccessSection
          newUser={newUser}
          setNewUser={setNewUser}
          onCreate={onCreate}
          onDeleteAll={onDeleteAll}
          accessList={accessList}
          accessData={accessData}
          onAddAll={onAddAll}
        />
      </div>
    </ModalBig>
  )
}

export const AccessSection = ({
  newUser,
  setNewUser,
  onCreate,
  onDeleteAll,
  onAddAll,
  accessList,
  accessData = [],
}) => {
  const userSelectStyles = useMemo(() => ({
    control: (base) => ({
      ...base,
      borderColor: '#4277FC',
      '&:hover': {
        ...base['&:hover'],
        borderColor: '#4277FC',
      },
    }),
    placeholder: (base) => ({
      ...base,
      color: '#414A5F',
      fontWeight: 400,
    }),
    singleValue: (base) => ({
      ...base,
      color: '#414A5F',
      fontWeight: 400,
    }),
    input: (base) => ({
      ...base,
      color: '#414A5F',
      fontWeight: 400,
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: '#414A5F',
      '&:hover': {
        ...base['&:hover'],
        color: '#414A5F',
      },
    }),
  }), [])
  const [removeAllDisabled, setRemoveAllDisabled] = useState(!accessData.some(isRemovableAccess))
  const [isToggleAllLoading, setIsToggleAllLoading] = useState(false)
  const [hasUsersToAdd, setHasUsersToAdd] = useState(true)
  const excludedUserIds = useMemo(() => {
    const strongestLevelByUser = new Map()

    accessData.forEach((item) => {
      const relation = item?.relation ?? item?.type
      const relationLevel = ACCESS_LEVELS[relation] || 0
      if (!relationLevel || item?.id == null) {
        return
      }
      const currentLevel = strongestLevelByUser.get(item.id) || 0
      strongestLevelByUser.set(item.id, Math.max(currentLevel, relationLevel))
    })

    return Array.from(strongestLevelByUser.entries())
      .filter(([, level]) => level >= MIN_EXCLUDED_LEVEL)
      .map(([id]) => id)
  }, [accessData])
  const userIdsKey = excludedUserIds.map((id) => String(id)).join(',')

  const normalizeId = (id) => {
    if (id == null) return []
    const numericId = Number(id)
    if (!Number.isNaN(numericId) && String(numericId) === String(id)) {
      return [id, numericId]
    }
    return [id]
  }

  const buildAccessOption = (user) => {
    const fullName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim()
    const email = user?.email || ''
    return {
      id: user?.id,
      name: fullName ? `${fullName} - (${email})` : email,
      type: ASSIGNABLE_RELATION,
    }
  }

  const fetchAllAssignableUsers = async() => {
    const users = []
    const pageSize = 100
    let offset = 0
    let totalCount = Infinity

    while (offset < totalCount) {
      const response = await UserService.find('', offset, pageSize, true)
      const chunk = response?.data || []
      totalCount = response?.meta?.count ?? chunk.length
      users.push(...chunk)

      if (!chunk.length || chunk.length < pageSize) {
        break
      }
      offset += chunk.length
    }

    return users
  }
  const getUsersToAdd = async() => {
    const users = await fetchAllAssignableUsers()
    const excludedIds = new Set(excludedUserIds.flatMap(normalizeId).map((id) => String(id)))

    return users
      .filter((user) => !excludedIds.has(String(user?.id)))
      .map(buildAccessOption)
  }
  const showRemoveAllAction = !removeAllDisabled || !hasUsersToAdd
  const classes = useStyles({ removeAllDisabled: !showRemoveAllAction })

  const addAllAvailableUsers = async() => {
    if (isToggleAllLoading) return

    setIsToggleAllLoading(true)
    try {
      const usersToAdd = await getUsersToAdd()
      setHasUsersToAdd(usersToAdd.length > 0)

      if (!usersToAdd.length) {
        return
      }

      onAddAll(usersToAdd, ASSIGNABLE_RELATION)
    } catch {
      toast.showToast({ type: 'error', message: lang.defaultErrorMessage })
    } finally {
      setIsToggleAllLoading(false)
    }
  }
  const handleChange = async(options) => {
    if (options.value === 'all') {
      setNewUser(null)
      await addAllAvailableUsers()
      return
    }

    const user = options?.user?.id
      ? {
        id: options.user.id,
        name: options.label,
        type: ASSIGNABLE_RELATION,
      }
      : null
    setNewUser(user)
  }
  const onToggleAllAccess = async() => {
    if (showRemoveAllAction) {
      onDeleteAll()
      return
    }

    await addAllAvailableUsers()
  }

  useEffect(() => {
    if (newUser) {
      onCreate?.(newUser.id, 'user', newUser.subType, ASSIGNABLE_RELATION)
    }
  }, [newUser])

  useEffect(() => {
    setRemoveAllDisabled(!accessData.some(isRemovableAccess))
  }, [accessData])
  useEffect(() => {
    let isCurrent = true

    const updateHasUsersToAdd = async() => {
      try {
        const usersToAdd = await getUsersToAdd()
        if (isCurrent) {
          setHasUsersToAdd(usersToAdd.length > 0)
        }
      } catch {
        if (isCurrent) {
          setHasUsersToAdd(true)
        }
      }
    }

    updateHasUsersToAdd()

    return () => {
      isCurrent = false
    }
  }, [userIdsKey])

  return (
    <>
      <div className={classes.userSelect}>
        <UserSelect
          styles={userSelectStyles}
          addAllOption={true}
          key={userIdsKey}
          exclude={excludedUserIds.flatMap(normalizeId)}
          onChange={handleChange}
          customSelectOptions={{
            noOptionsMessage: lang.folderAccessPermissions.noMoreUsers,
          }}
        />
      </div>
      <div className={classes.usersWithAccess}>
        {folderAccessPermissions.usersWithAccess}
        <div
          onClick={onToggleAllAccess}
          className={classes.removeAccessForAll}
          aria-disabled={isToggleAllLoading}
        >
          {showRemoveAllAction ? folderAccessPermissions.removeAccessForAll : folderAccessPermissions.addAccessForAll}
        </div>
      </div>
      <div className={classes.accessList}>
        {accessList}
      </div>
    </>
  )
}

export default AccessModal
